import axios, { AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';

import { AuthorizationStatusResponse } from '../domain/authorization';
import { OtpRequest, VerificationRequest } from '../domain/qualification';

export enum HeaderKey {
  AuthToken = 'x-coast-auth',
  VerificationToken = 'x-coast-verify',
  SetAuthToken = 'x-set-coast-auth',
  SetVerificationToken = 'x-set-coast-verify',
}

const configureAxios = (logout: () => void): void => {
  axios.interceptors.response.use(
    function (response: AxiosResponse) {
      const verification_token = response.headers[HeaderKey.SetVerificationToken] as string | undefined;
      const auth_token = response.headers[HeaderKey.SetAuthToken] as string | undefined;
      if (verification_token) {
        localStorage.setItem(HeaderKey.VerificationToken, verification_token);
      }

      if (auth_token) {
        localStorage.setItem(HeaderKey.AuthToken, auth_token);
      }

      return response;
    },
    function (error: AxiosError<{ error: string | undefined }>) {
      if (error.response?.status === 401) {
        logout();
      }

      const errorMessage = error.response?.data.error;
      if (errorMessage != null) {
        error.message = errorMessage;
      }

      return Promise.reject(error);
    },
  );

  axios.interceptors.request.use(
    function (request: InternalAxiosRequestConfig) {
      const verification_token = localStorage.getItem(HeaderKey.VerificationToken);
      const auth_token = localStorage.getItem(HeaderKey.AuthToken);

      if (verification_token) {
        request.headers[HeaderKey.VerificationToken] = verification_token;
      }

      if (auth_token) {
        request.headers[HeaderKey.AuthToken] = auth_token;
      }

      return request;
    },
    function (error: AxiosError<{ message: string }>) {
      const errorMessage = error.response?.data.message;
      if (errorMessage) {
        error.message = errorMessage;
      }

      return Promise.reject(error);
    },
  );
};

const requestVerification = async (request: VerificationRequest): Promise<AxiosResponse<void>> => {
  const body = {
    phoneNumber: request.phone,
    email: request.email,
    promoCode: request.promoCode,
    referralRockId: request.referrerId,
    partnerId: request.partnerId,
  };
  return await axios.post('/qualification/login/request-otp', body);
};

const requestEmbeddedVerification = async (request: VerificationRequest): Promise<AxiosResponse<void>> => {
  const { phone, email, embeddedSessionToken } = request;
  const body = {
    phoneNumber: phone,
    email,
  };
  return await axios.post('/qualification/login/embedded/request-otp', body, {
    headers: { Authorization: `Bearer ${embeddedSessionToken}` },
  });
};

const requestAuthorization = async (request: { otp: string }): Promise<AuthorizationStatusResponse> => {
  const axiosResponse = await axios.post<AuthorizationStatusResponse>('/qualification/login/verify-otp', request);
  return axiosResponse.data;
};

const resendVerification = async (): Promise<AxiosResponse<void>> => {
  return await axios.get('/qualification/login/resend-otp');
};

// V2 endpoints
const requestOtp = async (request: OtpRequest): Promise<AxiosResponse<void>> => {
  const body = {
    phoneNumber: request.phone,
    channel: request.channel,
  };
  return await axios.post('/qualification/login/request-otp-v2', body);
};

const resendOtp = async (request: { channel: OtpRequest['channel'] }): Promise<AxiosResponse<void>> => {
  return await axios.post('/qualification/login/resend-otp-v2', request);
};

const verifyOtp = async (request: { otp: string }): Promise<void> => {
  return await axios.post('/qualification/login/verify-otp-v2', request);
};

export {
  requestVerification,
  requestEmbeddedVerification,
  requestAuthorization,
  configureAxios,
  resendVerification,
  requestOtp,
  resendOtp,
  verifyOtp,
};
